import classNames from "classnames/bind";
import React, { useState } from "react";
import { Button } from "../../Button";
import { colours } from "../../constants/colours";
import { TextDataDisplay } from "../../DataDisplay/TextDataDisplay";
import { DropdownMenu } from "../../DropdownMenu";
import { Icon, IconLoading } from "../../Icon";
import { Skeleton } from "../../Skeleton";
import { Tooltip } from "../../Tooltip";
import styles from "./KeyValueTile.module.scss";
import type { KeyValueTileProps } from "./types";

const cx = classNames.bind(styles);

/**
 * A tile or collections of tiles to display important information
 */
const KeyValueTile = ({
  title,
  value,
  description,
  emptyText = "-",
  actions,
  action,
  isLoading = false,
  isBusy = false,
  className,
  style,
}: KeyValueTileProps) => {
  const [isActionsMenuOpen, setIsActionsMenuOpen] = useState(false);

  return (
    <div
      className={cx("container", className)}
      tabIndex={0}
      style={style}
    >
      <div className={cx("main")}>
        <div>
          <Skeleton
            isLoading={isLoading}
            isBusy={isBusy}
          >
            <p className={cx("title")}>
              <TextDataDisplay
                value={title}
                as="span"
              />
              {description ? (
                <Tooltip
                  title={title}
                  content={description}
                  contentClassName={cx("description-tooltip")}
                  position={["top", "bottom", "left", "right"]}
                >
                  <Icon
                    variant="info"
                    size={16}
                    colour={colours.navy["700"]}
                  />
                </Tooltip>
              ) : null}
            </p>
          </Skeleton>
          <div className={cx("value-display-container")}>
            {typeof value === "function" ? (
              value({ className: cx("value"), isLoading, isBusy })
            ) : (
              <Skeleton
                isLoading={isLoading}
                isBusy={isBusy}
              >
                <span className={cx("value")}>{value ?? emptyText}</span>
              </Skeleton>
            )}
          </div>
        </div>
        <Skeleton
          isLoading={isLoading}
          isBusy={isBusy}
        >
          <div
            className={cx(
              "actions",
              (isBusy || isActionsMenuOpen) && "always-visible"
            )}
          >
            {isBusy ? (
              <IconLoading speed="fast" />
            ) : actions ? (
              <DropdownMenu
                options={actions}
                rootProps={{ onOpenChange: setIsActionsMenuOpen }}
              >
                <Button
                  variant="outline"
                  icon="dots-three-vertical"
                  size="xx-small"
                />
              </DropdownMenu>
            ) : action ? (
              <Button
                {...action}
                size="xx-small"
              />
            ) : null}
          </div>
        </Skeleton>
      </div>
    </div>
  );
};

export default KeyValueTile;
